import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import { Sparkles, Wand2, FileText, TrendingUp, AlertCircle, Zap } from 'lucide-react';

const features = [
  {
    id: 'aiAutofillTriggered',
    label: 'AI Autofill Usage',
    icon: FileText,
    color: 'bg-emerald-500',
    gradient: 'from-emerald-500 to-emerald-600',
    description: 'Users who triggered AI autofill'
  },
  {
    id: 'aiResumeOptimizeApplied',
    label: 'AI Optimize Applied',
    icon: Wand2,
    color: 'bg-purple-500',
    gradient: 'from-purple-500 to-purple-600',
    description: 'Users who applied AI optimizations'
  }
];

const AIFeaturesChart = ({ data }) => {
  console.log('🔍 AIFeaturesChart received data:', data);

  if (!data || !data.totalUsers) {
    return (
      <Card className="relative overflow-hidden bg-card hover:bg-card/90 transition-all duration-300 border border-border rounded-xl shadow-sm hover:shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Features Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <div className="mx-auto w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <p className="text-muted-foreground font-medium">No AI data available</p>
              <p className="text-xs text-muted-foreground">
                AI features usage will appear here once users start using autofill and optimization features
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalUsers = data.totalUsers || 1; // Prevent division by zero
  const getPercentage = (value) => ((value / totalUsers) * 100).toFixed(1);

  // Check if there's any AI activity
  const hasAIActivity = data.aiAutofillTriggered > 0 || data.aiResumeOptimizeApplied > 0;

  return (
    <Card className="relative overflow-hidden backdrop-blur-md bg-card/40 hover:bg-card/60 dark:hover:bg-card/80 transition-all duration-300 border-border/30 hover:border-border/50 rounded-xl shadow-sm hover:shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.01] via-primary/[0.005] to-transparent pointer-events-none" />
      <CardHeader>
        <CardTitle className="text-sm font-medium text-foreground/90 dark:text-foreground/80 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          AI Features Usage
        </CardTitle>
        <div className="flex items-center gap-2 mt-2">
          <TrendingUp className="h-4 w-4 text-primary/60" />
          <span className="text-xs text-muted-foreground">
            AI Tool Usage Rate: {data.aiToolUsageRate || 0}%
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {!hasAIActivity ? (
          <div className="text-center py-6 space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-r from-primary/10 to-primary/20 rounded-full flex items-center justify-center">
              <Zap className="h-6 w-6 text-primary/60" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">No AI activity yet</p>
              <p className="text-xs text-muted-foreground">
                Users haven't started using AI features. This is normal for new deployments.
              </p>
            </div>
          </div>
        ) : (
          <>
            {features.map((feature) => {
              const value = data[feature.id] || 0;
              const percentage = getPercentage(value);
              const Icon = feature.icon;

              return (
                <div key={feature.id} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg bg-gradient-to-r", feature.gradient)}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">{feature.label}</span>
                        <span className="text-xs text-muted-foreground">{feature.description}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-right">
                      <div>
                        <span className="text-sm font-bold tabular-nums text-foreground">{value.toLocaleString()}</span>
                        <div className="text-xs text-muted-foreground">{percentage}% of users</div>
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <Progress
                      value={percentage}
                      className="h-2.5 bg-muted/30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rounded-full" />
                  </div>
                </div>
              );
            })}

            {/* Additional insights */}
            <div className="pt-4 border-t border-border/20">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="text-center p-3 bg-muted/20 rounded-lg">
                  <div className="text-muted-foreground mb-1">Total Autofill Events</div>
                  <div className="font-bold text-foreground text-lg">{data.totalAutofillEvents || 0}</div>
                </div>
                <div className="text-center p-3 bg-muted/20 rounded-lg">
                  <div className="text-muted-foreground mb-1">Total Optimize Events</div>
                  <div className="font-bold text-foreground text-lg">{data.totalOptimizeEvents || 0}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AIFeaturesChart; 